import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { filters } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import {
  getAllPushNotifications,
  updatePushNotificationStatus,
} from "store/actions/pushNotificationAction"
import { showAlert } from "store/reducers/alertReducer"
import ConfirmModal from "components/Common/ConfirmModal"
import useNotificationStatusLabel from "./useNotificationStatusLabel"

const AppNotifications = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("App Notifications ")
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()

  const {
    loading,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    isUpdating,
    pushNotifications,
  } = useSelector(state => state.PushNotifications)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })

  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [notificationToToggle, setNotificationToToggle] = useState({
    id: null,
    status: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      15,
      "notification_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      15,
      "notification_3",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  useEffect(() => {
    fetchAllNotification()
  }, [])

  const fetchAllNotification = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllPushNotifications(payload))
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllPushNotifications(payload))

    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
    setSearchTerm("")
    navigate(`/app-notifications?page=1`)
  }
  const getApiCall = (page = 1) => {
    const { status, sortOrder } = selectedFilters
    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    return dispatch(getAllPushNotifications(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/app-notifications?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall() // Default page = 1
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const renderNotificationType = useNotificationStatusLabel()

  const handleEnableNotification = (id, status) => {
    setNotificationToToggle({ id, status })
    setShowConfirmModal(true)
  }

  const confirmToggleNotification = () => {
    const { id, status } = notificationToToggle
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updatePushNotificationStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setShowConfirmModal(false)
        setNotificationToToggle({ id: null, status: null })
      })
      .catch(() => {
        setShowConfirmModal(false)
        setNotificationToToggle({ id: null, status: null })
      })
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("App Notifications")}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                />
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={7} />
                  ) : pushNotifications.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No app notifications found")}
                    </p>
                  ) : (
                    <Table className="table mb-0 capitalize_td">
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Title")}</th>
                          <th>{t("Message")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Updated At")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pushNotifications.map(
                          (
                            {
                              _id,
                              title,
                              body,
                              type,
                              actionType,
                              status,
                              hidden,
                              remark,
                              updatedAt,
                            },
                            index
                          ) => (
                            <React.Fragment key={_id}>
                              <tr
                                onClick={() =>
                                  navigate(
                                    `/app-notifications/app-notification-details?id=${_id}`
                                  )
                                }
                                className="cursor-pointer"
                              >
                                <td rowSpan={2}>
                                  {(currentPage - 1) * 10 + index + 1}
                                </td>

                                <td> {title?.en || "-"} </td>
                                <td>{body?.en || "-"}</td>
                                <td className="border-bottom-none">
                                  {(type === "ORDER_STATUS" && t("Order")) ||
                                    "-"}
                                </td>
                                <td className="border-bottom-none">
                                  {type === "ORDER_STATUS" &&
                                    renderNotificationType(actionType)}
                                </td>
                                <td rowSpan={2}>{formatDateTime(updatedAt)}</td>
                                <td className="border-bottom-none">
                                  {status === 1 ? (
                                    <Badge className="bg-success">
                                      {t("Active")}
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-danger">
                                      {t("Deactive")}
                                    </Badge>
                                  )}
                                </td>
                                <td
                                  rowSpan={2}
                                  className="d-flex align-items-center gap-2 border-bottom-none"
                                >
                                  <div className="gap-1 d-flex">
                                    {usersPermissions.edit && (
                                      <Switch
                                        onClickHandler={e => {
                                          e.stopPropagation()
                                          handleEnableNotification(_id, status)
                                        }}
                                        isChecked={status === 1}
                                        disabled={
                                          _id === selectedId && isUpdating
                                        }
                                      />
                                    )}
                                    <UncontrolledDropdown>
                                      <DropdownToggle
                                        tag="a"
                                        className="card-drop"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <i className="mdi mdi-dots-vertical font-size-18"></i>
                                      </DropdownToggle>
                                      <DropdownMenu className="dropdown-menu-end">
                                        {usersPermissions.edit && (
                                          <DropdownItem
                                            onClick={e => {
                                              e.stopPropagation()
                                              navigate(
                                                `/app-notifications/edit?id=${_id}`
                                              )
                                            }}
                                          >
                                            <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                            {t("Edit")}
                                          </DropdownItem>
                                        )}
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              `/app-notifications/app-notification-details?id=${_id}`
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                          {t("View")}
                                        </DropdownItem>
                                        {/* )} */}
                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                {/* <td></td> */}
                                <td>{title?.ar || "-"}</td>
                                <td>{body?.ar || "-"}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>
                            </React.Fragment>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && (
                  <Card>
                    <CardBody>
                      <Paginations
                        data={pushNotifications}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </CardBody>
                  </Card>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <ConfirmModal
        show={showConfirmModal}
        isLoading={isUpdating}
        onDeleteClick={confirmToggleNotification}
        onCloseClick={() => setShowConfirmModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </div>
  )
}

export default AppNotifications
